import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {apiService} from '../services/api';
import {useAdmin} from './AdminContext';
import {formatDate, formatPrice} from './adminUtils';

interface AdminUserSummary {
  id: string;
  email: string;
  name?: string | null;
  provider?: string | null;
  avatarUrl?: string | null;
  tokens: number;
  isActive: boolean;
  createdAt?: string | null;
  lastLogin?: string | null;
  updatedAt?: string | null;
  adminNote?: string | null;
  deactivatedAt?: string | null;
  deactivationReason?: string | null;
  favoritesCount: number;
  listingsCount: number;
  purchasedContactsCount: number;
  ordersCount: number;
}

interface AdminListing {
  id: string;
  title: string;
  city?: string | null;
  price?: number | null;
  currency?: string | null;
  purpose?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
}

interface AdminUserOrder {
  id: string;
  status: string;
  paymentStatus: string;
  paymentMethod?: string | null;
  totalAmount: number;
  createdAt: string;
  paymentId?: string | null;
  test: boolean;
}

interface AdminPurchasedContact {
  listing: AdminListing;
  tokensUsed: number;
  purchasedAt?: string | null;
}

interface AdminTokenAdjustment {
  id: string;
  adminId: string;
  delta: number;
  reason?: string | null;
  createdAt: string;
}

interface AdminUserDetail {
  user: AdminUserSummary;
  listings: AdminListing[];
  purchasedContacts: AdminPurchasedContact[];
  favorites: AdminListing[];
  orders: AdminUserOrder[];
  tokenAdjustments: AdminTokenAdjustment[];
}

const AdminUsersPage: React.FC = () => {
  const {token} = useAdmin();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [tokenDelta, setTokenDelta] = useState('');
  const [tokenReason, setTokenReason] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'listings' | 'contacts' | 'favorites' | 'orders' | 'tokens'>('overview');
  const [deletingUser, setDeletingUser] = useState(false);

  const activeParam = useMemo(() => {
    if (activeFilter === 'active') return true;
    if (activeFilter === 'inactive') return false;
    return undefined;
  }, [activeFilter]);

  const loadUsers = useCallback(async () => {
    if (!token) return;
    setLoadingUsers(true);
    setDataError(null);
    try {
      const data = await apiService.adminGetUsersFiltered(token, {
        q: query.trim() || undefined,
        active: activeParam,
      });
      setUsers(data);
      if (data.length) {
        setSelectedUserId((prev) => prev ?? data[0].id);
      } else {
        setSelectedUserId(null);
        setSelectedUser(null);
      }
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesno ucitavanje korisnika.');
    } finally {
      setLoadingUsers(false);
    }
  }, [token, query, activeParam]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadUserDetails = useCallback(async (userId: string) => {
    if (!token) return;
    setLoadingDetails(true);
    setDataError(null);
    try {
      const details = await apiService.adminGetUserDetails(token, userId);
      setSelectedUser(details);
      setNoteDraft(details.user.adminNote || '');
      setStatusReason(details.user.deactivationReason || '');
    } catch (error) {
      setSelectedUser(null);
      setDataError(error instanceof Error ? error.message : 'Neuspesno ucitavanje detalja korisnika.');
    } finally {
      setLoadingDetails(false);
    }
  }, [token]);

  useEffect(() => {
    if (!selectedUserId) return;
    loadUserDetails(selectedUserId);
  }, [loadUserDetails, selectedUserId]);

  useEffect(() => {
    setActiveTab('overview');
  }, [selectedUserId]);

  const handleToggleStatus = async () => {
    if (!token || !selectedUser) return;
    setDataError(null);
    try {
      await apiService.adminUpdateUserStatus(token, selectedUser.user.id, !selectedUser.user.isActive, statusReason.trim() || undefined);
      await loadUsers();
      await loadUserDetails(selectedUser.user.id);
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesna promena statusa.');
    }
  };

  const handleAdjustTokens = async () => {
    if (!token || !selectedUser) return;
    const delta = Number(tokenDelta);
    if (Number.isNaN(delta) || delta === 0) {
      setDataError('Delta mora biti broj razlicit od nule.');
      return;
    }
    setDataError(null);
    try {
      await apiService.adminAdjustUserTokens(token, selectedUser.user.id, delta, tokenReason.trim() || undefined);
      setTokenDelta('');
      setTokenReason('');
      await loadUsers();
      await loadUserDetails(selectedUser.user.id);
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesna izmena tokena.');
    }
  };

  const handleSaveNote = async () => {
    if (!token || !selectedUser) return;
    setDataError(null);
    try {
      await apiService.adminUpdateUserNote(token, selectedUser.user.id, noteDraft.trim() || undefined);
      await loadUsers();
      await loadUserDetails(selectedUser.user.id);
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesno cuvanje beleške.');
    }
  };

  const handleDeleteUser = async () => {
    if (!token || !selectedUser || deletingUser) return;
    const confirmed = window.confirm(
      'Brisanjem korisnika bice obrisani i njegovi orderi, nekretnine i liste. Da li ste sigurni?'
    );
    if (!confirmed) return;
    setDeletingUser(true);
    setDataError(null);
    try {
      await apiService.adminDeleteUser(token, selectedUser.user.id);
      setSelectedUserId(null);
      setSelectedUser(null);
      await loadUsers();
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesno brisanje korisnika.');
    } finally {
      setDeletingUser(false);
    }
  };

  const selectedSummary = useMemo(
    () => users.find((user) => user.id === selectedUserId) || null,
    [users, selectedUserId]
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-neutral-900">Korisnici</h2>
        <p className="mt-1 text-neutral-500">Pretraga, status i tokeni korisnika.</p>
      </div>

      {dataError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {dataError}
        </div>
      )}

      <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="input-field max-w-sm"
            placeholder="Pretraga po emailu ili imenu"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <select
            className="input-field max-w-[180px]"
            value={activeFilter}
            onChange={(event) => setActiveFilter(event.target.value as 'all' | 'active' | 'inactive')}
          >
            <option value="all">Svi statusi</option>
            <option value="active">Aktivni</option>
            <option value="inactive">Blokirani</option>
          </select>
          <button className="btn-outline" type="button" onClick={loadUsers} disabled={loadingUsers}>
            {loadingUsers ? 'Ucitavam...' : 'Primeni filtere'}
          </button>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[380px_1fr]">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-neutral-900">Lista korisnika</h3>
            <span className="text-sm text-neutral-500">{users.length}</span>
          </div>
          {loadingUsers ? (
            <div className="space-y-3">
              <div className="h-16 rounded-xl bg-neutral-100" />
              <div className="h-16 rounded-xl bg-neutral-100" />
              <div className="h-16 rounded-xl bg-neutral-100" />
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => setSelectedUserId(user.id)}
                  className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                    user.id === selectedUserId
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-neutral-900">{user.name || 'Bez imena'}</p>
                      <p className="text-sm text-neutral-500">{user.email}</p>
                    </div>
                    <span className={`text-xs font-semibold ${user.isActive ? 'text-primary-500' : 'text-red-500'}`}>
                      {user.isActive ? 'Aktivan' : 'Blokiran'}
                    </span>
                  </div>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-neutral-500">
                    <span>Nekretnine: {user.listingsCount}</span>
                    <span>Kontakti: {user.purchasedContactsCount}</span>
                    <span>Orderi: {user.ordersCount}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-neutral-900">
                  {selectedSummary?.name || 'Korisnik'}
                </h3>
                <p className="text-sm text-neutral-500">{selectedSummary?.email}</p>
              </div>
              <div className="flex flex-wrap gap-3 text-sm text-neutral-500">
                <span>Tokeni: {selectedSummary?.tokens ?? '-'}</span>
                <span>Registracija: {formatDate(selectedSummary?.createdAt)}</span>
                <span>Poslednji login: {formatDate(selectedSummary?.lastLogin)}</span>
              </div>
            </div>

            {loadingDetails ? (
              <div className="h-32 rounded-xl bg-neutral-100" />
            ) : !selectedUser ? (
              <p className="text-neutral-500">Izaberite korisnika da vidite detalje.</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                  <p className="text-sm text-neutral-500">Nekretnine</p>
                  <p className="text-3xl font-semibold text-neutral-900">{selectedUser.listings.length}</p>
                </div>
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                  <p className="text-sm text-neutral-500">Kupljeni kontakti</p>
                  <p className="text-3xl font-semibold text-neutral-900">{selectedUser.purchasedContacts.length}</p>
                </div>
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                  <p className="text-sm text-neutral-500">Favoriti</p>
                  <p className="text-3xl font-semibold text-neutral-900">{selectedUser.favorites.length}</p>
                </div>
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                  <p className="text-sm text-neutral-500">Orderi</p>
                  <p className="text-3xl font-semibold text-neutral-900">{selectedUser.orders.length}</p>
                </div>
              </div>
            )}
          </div>

          {selectedUser && (
            <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
              <div className="flex flex-wrap gap-2">
                <button
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                    activeTab === 'overview' ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-600'
                  }`}
                  type="button"
                  onClick={() => setActiveTab('overview')}
                >
                  Pregled
                </button>
                <button
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                    activeTab === 'listings' ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-600'
                  }`}
                  type="button"
                  onClick={() => setActiveTab('listings')}
                >
                  Nekretnine ({selectedUser.listings.length})
                </button>
                <button
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                    activeTab === 'contacts' ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-600'
                  }`}
                  type="button"
                  onClick={() => setActiveTab('contacts')}
                >
                  Kupljeni kontakti ({selectedUser.purchasedContacts.length})
                </button>
                <button
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                    activeTab === 'favorites' ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-600'
                  }`}
                  type="button"
                  onClick={() => setActiveTab('favorites')}
                >
                  Favoriti ({selectedUser.favorites.length})
                </button>
                <button
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                    activeTab === 'orders' ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-600'
                  }`}
                  type="button"
                  onClick={() => setActiveTab('orders')}
                >
                  Orderi ({selectedUser.orders.length})
                </button>
                <button
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                    activeTab === 'tokens' ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-600'
                  }`}
                  type="button"
                  onClick={() => setActiveTab('tokens')}
                >
                  Token izmene ({selectedUser.tokenAdjustments.length})
                </button>
              </div>
            </div>
          )}

          {selectedUser && activeTab === 'overview' && (
            <div className="grid gap-6 lg:grid-cols-1">
              <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
                <h4 className="text-xl font-semibold text-neutral-900">Administracija</h4>
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                    <p className="text-sm text-neutral-500">Status naloga</p>
                    <p className="mt-2 text-xl font-semibold text-neutral-900">
                      {selectedUser.user.isActive ? 'Aktivan' : 'Blokiran'}
                    </p>
                    <input
                      className="input-field mt-3"
                      placeholder="Razlog blokade (opciono)"
                      value={statusReason}
                      onChange={(event) => setStatusReason(event.target.value)}
                    />
                    <button className="btn-outline mt-3 w-full" type="button" onClick={handleToggleStatus}>
                      {selectedUser.user.isActive ? 'Blokiraj' : 'Aktiviraj'}
                    </button>
                    <p className="mt-3 text-xs text-neutral-500">
                      Aktivni korisnik ima pun pristup. Blokirani korisnik ne moze da se prijavi ni registruje, a njegove
                      nekretnine se automatski sklanjaju dok je blokiran i vracaju po aktivaciji.
                    </p>
                  </div>
                  <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                    <p className="text-sm text-neutral-500">Tokeni</p>
                    <p className="mt-2 text-xl font-semibold text-neutral-900">{selectedUser.user.tokens}</p>
                    <input
                      className="input-field mt-3"
                      placeholder="Delta (+/-)"
                      value={tokenDelta}
                      onChange={(event) => setTokenDelta(event.target.value)}
                    />
                    <input
                      className="input-field mt-2"
                      placeholder="Razlog (opciono)"
                      value={tokenReason}
                      onChange={(event) => setTokenReason(event.target.value)}
                    />
                    <button className="btn-outline mt-3 w-full" type="button" onClick={handleAdjustTokens}>
                      Sacuvaj promenu
                    </button>
                  </div>
                </div>
                <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                  <p className="text-sm text-neutral-500">Admin beleska</p>
                  <textarea
                    className="input-field mt-3 min-h-[120px]"
                    placeholder="Upisi internu belesku..."
                    value={noteDraft}
                    onChange={(event) => setNoteDraft(event.target.value)}
                  />
                  <button className="btn-outline mt-3" type="button" onClick={handleSaveNote}>
                    Sacuvaj belesku
                  </button>
                </div>
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-5">
                  <p className="text-sm text-red-700">Trajno brisanje korisnika</p>
                  <p className="mt-2 text-xs text-red-600">
                    Ova akcija uklanja korisnika, njegove ordere, nekretnine i liste, kao i nalog u Supabase auth-u.
                  </p>
                  <button
                    className="btn-outline mt-3 w-full border-red-300 text-red-600 hover:bg-red-100"
                    type="button"
                    onClick={handleDeleteUser}
                    disabled={deletingUser}
                  >
                    {deletingUser ? 'Brisem...' : 'Obrisi korisnika'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedUser && activeTab === 'tokens' && (
            <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
              <h4 className="text-xl font-semibold text-neutral-900">Token prilagodjavanja</h4>
              <div className="mt-4 overflow-hidden rounded-xl border border-neutral-200">
                {selectedUser.tokenAdjustments.length ? (
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-neutral-500">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Delta</th>
                        <th className="px-4 py-3 text-left font-medium">Datum</th>
                        <th className="px-4 py-3 text-left font-medium">Razlog</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedUser.tokenAdjustments.map((entry) => (
                        <tr key={entry.id} className="border-t border-neutral-200">
                          <td className="px-4 py-3 font-semibold text-neutral-900">
                            {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
                          </td>
                          <td className="px-4 py-3 text-neutral-600">{formatDate(entry.createdAt)}</td>
                          <td className="px-4 py-3 text-neutral-600">{entry.reason || 'Bez razloga'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-4 py-4 text-sm text-neutral-500">Nema izmena.</div>
                )}
              </div>
            </div>
          )}

          {selectedUser && activeTab === 'listings' && (
            <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
              <h4 className="text-xl font-semibold text-neutral-900">Nekretnine korisnika</h4>
              <div className="mt-4">
                {selectedUser.listings.length ? (
                  <div className="overflow-hidden rounded-xl border border-neutral-200">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 text-neutral-500">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Nekretnina</th>
                          <th className="px-4 py-3 text-left font-medium">Grad</th>
                          <th className="px-4 py-3 text-left font-medium">Cena</th>
                          <th className="px-4 py-3 text-left font-medium">Tip</th>
                          <th className="px-4 py-3 text-left font-medium">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUser.listings.map((listing) => (
                          <tr key={listing.id} className="border-t border-neutral-200">
                            <td className="px-4 py-3 font-semibold text-neutral-900">{listing.title}</td>
                            <td className="px-4 py-3 text-neutral-600">{listing.city || '-'}</td>
                            <td className="px-4 py-3 text-neutral-600">{formatPrice(listing.price, listing.currency)}</td>
                            <td className="px-4 py-3 text-neutral-600">{listing.purpose || '-'}</td>
                            <td className="px-4 py-3">
                              <a
                                className="btn-outline text-xs"
                                href={`/property/${listing.id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Pogledaj
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500">Nema unetih nekretnina.</p>
                )}
              </div>
            </div>
          )}

          {selectedUser && activeTab === 'contacts' && (
            <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
              <h4 className="text-xl font-semibold text-neutral-900">Kupljeni kontakti</h4>
              <div className="mt-4">
                {selectedUser.purchasedContacts.length ? (
                  <div className="overflow-hidden rounded-xl border border-neutral-200">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 text-neutral-500">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Nekretnina</th>
                          <th className="px-4 py-3 text-left font-medium">Tokeni</th>
                          <th className="px-4 py-3 text-left font-medium">Datum</th>
                          <th className="px-4 py-3 text-left font-medium">Kontakt</th>
                          <th className="px-4 py-3 text-left font-medium">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUser.purchasedContacts.map((entry) => (
                          <tr key={entry.listing.id} className="border-t border-neutral-200">
                            <td className="px-4 py-3 font-semibold text-neutral-900">{entry.listing.title}</td>
                            <td className="px-4 py-3 text-neutral-600">{entry.tokensUsed}</td>
                            <td className="px-4 py-3 text-neutral-600">{formatDate(entry.purchasedAt)}</td>
                            <td className="px-4 py-3 text-neutral-600">
                              {entry.listing.contact_email || '-'} / {entry.listing.contact_phone || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <a
                                className="btn-outline text-xs"
                                href={`/property/${entry.listing.id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Pogledaj
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500">Nema kupljenih kontakata.</p>
                )}
              </div>
            </div>
          )}

          {selectedUser && activeTab === 'favorites' && (
            <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
              <h4 className="text-xl font-semibold text-neutral-900">Favoriti</h4>
              <div className="mt-4">
                {selectedUser.favorites.length ? (
                  <div className="overflow-hidden rounded-xl border border-neutral-200">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 text-neutral-500">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Nekretnina</th>
                          <th className="px-4 py-3 text-left font-medium">Grad</th>
                          <th className="px-4 py-3 text-left font-medium">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUser.favorites.map((listing) => (
                          <tr key={listing.id} className="border-t border-neutral-200">
                            <td className="px-4 py-3 font-semibold text-neutral-900">{listing.title}</td>
                            <td className="px-4 py-3 text-neutral-600">{listing.city || '-'}</td>
                            <td className="px-4 py-3">
                              <a
                                className="btn-outline text-xs"
                                href={`/property/${listing.id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Pogledaj
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500">Nema favorita.</p>
                )}
              </div>
            </div>
          )}

          {selectedUser && activeTab === 'orders' && (
            <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
              <h4 className="text-xl font-semibold text-neutral-900">Orderi</h4>
              <div className="mt-4">
                {selectedUser.orders.length ? (
                  <div className="overflow-hidden rounded-xl border border-neutral-200">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 text-neutral-500">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Order</th>
                          <th className="px-4 py-3 text-left font-medium">Status</th>
                          <th className="px-4 py-3 text-left font-medium">Placanje</th>
                          <th className="px-4 py-3 text-left font-medium">Ukupno</th>
                          <th className="px-4 py-3 text-left font-medium">Datum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUser.orders.map((order) => (
                          <tr key={order.id} className="border-t border-neutral-200">
                            <td className="px-4 py-3 font-semibold text-neutral-900">{order.id.slice(0, 8)}</td>
                            <td className="px-4 py-3 text-neutral-600">{order.status}</td>
                            <td className="px-4 py-3 text-neutral-600">{order.paymentStatus}</td>
                            <td className="px-4 py-3 text-neutral-600">{formatPrice(order.totalAmount, null)}</td>
                            <td className="px-4 py-3 text-neutral-600">{formatDate(order.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500">Nema ordera.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
